import React, { useMemo } from 'react';
import {
  MigrationFlag,
  MigrationFlagsContext,
  useMigrationFlags,
} from './migration-flags';

/**
 * Mode applied when multiple migration flags are supplied.
 *
 * - `all` (default): every flag must evaluate to `true` for the migration
 *   to be considered enabled. Safest default — the new path only kicks in
 *   when every gate has been opened.
 * - `any`: the migration is enabled as soon as any one of the flags
 *   evaluates to `true`. Useful for gradual rollouts where multiple
 *   experiments converge on the same code path.
 */
export type MigrationMode = 'all' | 'any';

type SingleNameProps = {
  name: MigrationFlag;
  names?: never;
  mode?: never;
};

type MultiNameProps = {
  name?: never;
  names: readonly MigrationFlag[];
  mode?: MigrationMode;
};

type CommonProps = {
  on: React.ReactNode;
  off: React.ReactNode;
  /**
   * Optional override for the loading state. By default, while the flag
   * service is being queried, the migration renders the `off` branch.
   * This keeps the legacy (pre-migration) experience as the safe default
   * and avoids flashing the new UI before we know the flag value.
   */
  loading?: React.ReactNode;
  context?: MigrationFlagsContext;
};

export type MigrationProps = CommonProps & (SingleNameProps | MultiNameProps);

/**
 * Renders the `on` branch when the configured migration flag(s) are
 * enabled for the current user, and the `off` branch otherwise.
 *
 * The set of available flag names is constrained to the entries in
 * `MIGRATION_FLAGS` (see `migration-flags.ts`). Add new migrations to
 * that registry — never pass arbitrary flag strings here.
 *
 * Single-flag form:
 * ```tsx
 * <Migration
 *   name="migration-shadcn-checkbox"
 *   on={<NewCheckbox {...props} />}
 *   off={<LegacyCheckbox {...props} />}
 * />
 * ```
 *
 * Multi-flag form (e.g. an experiment combined with a kill-switch):
 * ```tsx
 * <Migration
 *   names={['migration-shadcn-modal', 'migration-shadcn-kill-switch-off']}
 *   mode="all"
 *   on={<NewModal {...props} />}
 *   off={<LegacyModal {...props} />}
 * />
 * ```
 *
 * Route-level usage is intentionally supported — both branches accept
 * any `ReactNode`, so pages can be migrated by composing entire route
 * subtrees instead of individual components.
 */
export const Migration = ({
  name,
  names,
  mode = 'all',
  on,
  off,
  loading,
  context,
}: MigrationProps) => {
  const { flags, isLoading } = useMigrationFlags(context);

  const resolved = useMemo<readonly MigrationFlag[]>(() => {
    if (name) return [name];
    return names ?? [];
  }, [name, names]);

  const isEnabled = useMemo(() => {
    if (!flags || resolved.length === 0) return false;
    if (mode === 'any') {
      return resolved.some(n => flags[n] === true);
    }
    return resolved.every(n => flags[n] === true);
  }, [flags, mode, resolved]);

  if (isLoading) {
    return <>{loading ?? off}</>;
  }

  return <>{isEnabled ? on : off}</>;
};

Migration.displayName = 'Migration';

type CreateMigrationCommon<P> = {
  on: React.ComponentType<P>;
  off: React.ComponentType<P>;
  /**
   * Optional component rendered while flags load. When omitted, the
   * `off` component is used — matches the `<Migration>` runtime default.
   */
  loading?: React.ComponentType<P>;
  /** Overrides the auto-generated `displayName` of the returned component. */
  displayName?: string;
};

type CreateMigrationSingle<P> = CreateMigrationCommon<P> & {
  name: MigrationFlag;
  names?: never;
  mode?: never;
};

type CreateMigrationMulti<P> = CreateMigrationCommon<P> & {
  name?: never;
  names: readonly MigrationFlag[];
  mode?: MigrationMode;
};

export type CreateMigrationOptions<P> =
  | CreateMigrationSingle<P>
  | CreateMigrationMulti<P>;

/**
 * Factory for migrating a shared component in a single place.
 *
 * Apply this once at the shared component's export site — every app
 * that imports the component automatically participates in the
 * migration without any call-site changes. Both `on` and `off`
 * components receive the same props, so they must share a prop
 * signature (wrap one in an adapter if the APIs diverge).
 *
 * @example
 * ```tsx
 * // internal/shared/components/Base/Checkbox.tsx
 * import { createMigration } from '../../utilities/Migration';
 * import { CheckboxLegacy } from './Checkbox.legacy';
 * import { CheckboxNew } from './Checkbox.new';
 *
 * export const Checkbox = createMigration({
 *   name: 'migration-shadcn-checkbox',
 *   on: CheckboxNew,
 *   off: CheckboxLegacy,
 * });
 * ```
 */
export function createMigration<P extends object>(
  options: CreateMigrationSingle<P>
): React.ComponentType<P>;
export function createMigration<P extends object>(
  options: CreateMigrationMulti<P>
): React.ComponentType<P>;
export function createMigration<P extends object>(
  options: CreateMigrationOptions<P>
): React.ComponentType<P> {
  type Normalised = {
    on: React.ComponentType<P>;
    off: React.ComponentType<P>;
    loading?: React.ComponentType<P>;
    displayName?: string;
    name?: MigrationFlag;
    names?: readonly MigrationFlag[];
    mode?: MigrationMode;
  };
  const {
    on: OnComponent,
    off: OffComponent,
    loading: LoadingComponent,
    displayName,
    name,
    names,
    mode,
  } = options as Normalised;

  const MigratedComponent = (props: P) => {
    const on = <OnComponent {...props} />;
    const off = <OffComponent {...props} />;
    const loading = LoadingComponent ? (
      <LoadingComponent {...props} />
    ) : undefined;

    if (name) {
      return <Migration name={name} on={on} off={off} loading={loading} />;
    }

    return (
      <Migration
        names={names ?? []}
        mode={mode}
        on={on}
        off={off}
        loading={loading}
      />
    );
  };

  const label = displayName ?? name ?? (names ?? []).join(',');
  MigratedComponent.displayName = `Migrated(${label})`;

  return MigratedComponent;
}

export default Migration;
